import { TOPIC_GRAPH_COMMENT_TYPE, TopicGraphNodeData, TopicGraphValidationError } from './types';

const QUESTION_TYPE = {
  Multiselect: 'MULTISELECT',
  PersonMultiselect: 'PERSON_MULTISELECT',
  Scale: 'SCALE',
  Ending: 'ENDING',
} as const;

const getQuestionNodes = (nodes: TopicGraphNodeData[]): TopicGraphNodeData[] =>
  nodes.filter((node) => node.type !== TOPIC_GRAPH_COMMENT_TYPE);

const validateGraphStructure = (allNodes: TopicGraphNodeData[], errors: TopicGraphValidationError[]): void => {
  const questionNodes = getQuestionNodes(allNodes);
  if (questionNodes.length === 0) {
    return;
  }

  const questionIndexes = new Set(questionNodes.map((node) => node.questionIndex));
  const startNodes = questionNodes.filter((node) => node.input === false);
  const endingNodes = questionNodes.filter((node) => node.type === QUESTION_TYPE.Ending);

  if (startNodes.length === 0) {
    errors.push({
      questionIndex: questionNodes[0].questionIndex,
      part: 'graph',
      error: 'validationMissingStartNode',
    });
  } else if (startNodes.length > 1) {
    startNodes.forEach((node) => {
      errors.push({
        questionIndex: node.questionIndex,
        part: 'input',
        error: 'validationMultipleStartNodes',
      });
    });
  }

  if (endingNodes.length === 0) {
    errors.push({
      questionIndex: questionNodes[0].questionIndex,
      part: 'graph',
      error: 'validationMissingEndingNode',
    });
  } else if (endingNodes.length > 1) {
    endingNodes.forEach((node) => {
      errors.push({
        questionIndex: node.questionIndex,
        part: 'output',
        error: 'validationMultipleEndingNodes',
      });
    });
  }

  endingNodes.forEach((node) => {
    if (!node.input) {
      errors.push({
        questionIndex: node.questionIndex,
        part: 'input',
        error: 'validationEndingMustHaveInput',
      });
    }
    if (Object.keys(node.outputs).length > 0) {
      errors.push({
        questionIndex: node.questionIndex,
        part: 'output',
        error: 'validationEndingMustHaveNoOutputs',
      });
    }
  });

  questionNodes.forEach((node) => {
    Object.entries(node.outputs).forEach(([key, value]) => {
      if (value != null && !questionIndexes.has(value)) {
        errors.push({
          questionIndex: node.questionIndex,
          error: 'validationNoConnection',
          part: `output-${key}`,
        });
      }
    });
  });

  validateReachabilityFromStart(questionNodes, startNodes, questionIndexes, errors);
};

const validateReachabilityFromStart = (
  questionNodes: TopicGraphNodeData[],
  startNodes: TopicGraphNodeData[],
  questionIndexes: Set<number>,
  errors: TopicGraphValidationError[],
): void => {
  if (startNodes.length !== 1) {
    return;
  }

  const nodeByIndex = new Map(questionNodes.map((node) => [node.questionIndex, node]));
  const reachable = new Set<number>();
  const queue = [startNodes[0].questionIndex];

  while (queue.length > 0) {
    const currentIndex = queue.shift()!;
    if (reachable.has(currentIndex)) {
      continue;
    }
    reachable.add(currentIndex);

    const node = nodeByIndex.get(currentIndex);
    if (!node) {
      continue;
    }

    Object.values(node.outputs).forEach((targetIndex) => {
      if (targetIndex != null && questionIndexes.has(targetIndex) && !reachable.has(targetIndex)) {
        queue.push(targetIndex);
      }
    });
  }

  questionNodes.forEach((node) => {
    if (!reachable.has(node.questionIndex)) {
      errors.push({
        questionIndex: node.questionIndex,
        part: 'input',
        error: 'validationUnreachableFromStart',
      });
    }
  });
};

/**
 * Validates plain topic graph node data (no Rete dependencies).
 * Used by API (retry loop) and Admin Ariel (client safety net).
 */
export const validateTopicGraph = (
  nodes: TopicGraphNodeData[],
  allNodes: TopicGraphNodeData[],
): TopicGraphValidationError[] => {
  const errors: TopicGraphValidationError[] = [];

  nodes.forEach((node) => {
    if (node.type === TOPIC_GRAPH_COMMENT_TYPE) {
      return;
    }

    if (node.questionText.trim().length === 0) {
      errors.push({ questionIndex: node.questionIndex, error: 'validationEmpty', part: 'questionText' });
    }

    Object.entries(node.choices).forEach(([choiceKey, choiceText]) => {
      if (choiceText.trim().length === 0) {
        errors.push({
          questionIndex: node.questionIndex,
          error: 'validationEmpty',
          part: `choice-${choiceKey}`,
        });
      }
    });

    if (node.type === QUESTION_TYPE.Scale) {
      const stmts = (node.statements ?? []).filter((s): s is string => s != null);
      stmts.forEach((statementText, index) => {
        if (statementText.trim().length === 0) {
          errors.push({
            questionIndex: node.questionIndex,
            error: 'validationEmpty',
            part: `statement-${index}`,
          });
        }
      });
    }

    if (node.reportOrder === null) {
      errors.push({ questionIndex: node.questionIndex, error: 'validationEmpty', part: 'reportOrder' });
    }

    if (
      node.input &&
      allNodes.some((otherNode) => Object.values(otherNode.outputs).includes(node.questionIndex)) === false
    ) {
      errors.push({ questionIndex: node.questionIndex, error: 'validationNoConnection', part: 'input' });
    }

    const hasChoiceOptions = Object.keys(node.choices).length > 0;
    Object.entries(node.outputs).forEach(([key, value]) => {
      if (hasChoiceOptions && !(key in node.choices)) {
        return;
      }
      if (value == null) {
        errors.push({
          questionIndex: node.questionIndex,
          error: 'validationNoConnection',
          part: `output-${key}`,
        });
      }
    });

    // When a node branches per choice (more than one output), every choice must have its own
    // output. A patch that keeps `choices.opt-1` but drops `outputs.opt-1` entirely would otherwise
    // pass validation with a dangling branch, since the loop above only checks outputs that exist.
    if (hasChoiceOptions && Object.keys(node.outputs).length > 1) {
      Object.keys(node.choices).forEach((choiceKey) => {
        if (!(choiceKey in node.outputs)) {
          errors.push({
            questionIndex: node.questionIndex,
            error: 'validationNoConnection',
            part: `output-${choiceKey}`,
          });
        }
      });
    }

    if (
      allNodes.some(
        (otherNode) => otherNode.reportOrder === node.reportOrder && otherNode.questionIndex !== node.questionIndex,
      )
    ) {
      errors.push({
        questionIndex: node.questionIndex,
        error: 'validationReportOrderUnique',
        part: 'reportOrder',
      });
    }

    if (node.type === QUESTION_TYPE.Multiselect || node.type === QUESTION_TYPE.PersonMultiselect) {
      if (node.range && node.range.max < node.range.min) {
        errors.push({ questionIndex: node.questionIndex, error: 'validationMaxLesserThanMin', part: 'min/max' });
      }
    }

    if (node.type === QUESTION_TYPE.Multiselect && node.range) {
      if (node.range.min < 1) {
        errors.push({ questionIndex: node.questionIndex, error: 'validationMinLesserThan1', part: 'min' });
      }
      if (node.range.max < 2) {
        errors.push({ questionIndex: node.questionIndex, error: 'validationMaxLesserThan2', part: 'max' });
      }
      if (node.range.max > Object.keys(node.choices).length) {
        errors.push({
          questionIndex: node.questionIndex,
          error: 'validationMaxGreaterThanOptions',
          part: 'max',
        });
      }
    }

    if (node.type === QUESTION_TYPE.PersonMultiselect && node.range) {
      if (node.range.min < 0) {
        errors.push({ questionIndex: node.questionIndex, error: 'validationMinLesserThan0', part: 'min' });
      }
      if (node.range.max < 1) {
        errors.push({ questionIndex: node.questionIndex, error: 'validationMaxLesserThan1', part: 'max' });
      }
    }
  });

  validateGraphStructure(allNodes, errors);

  return errors;
};
